import analytics from '../../utils/analytics';
import camelCase from 'lodash/camelCase';
import {
    resolveAnalyticsUrl,
    extractContentfulEntryId
} from '../../utils/content-analytics-resources';

export interface ContentLinkAnalytics {
    /** The type of link being clicked ('card' or 'text-link') */
    linkType?: 'card' | 'text-link';
    /** The context where the link appears ('search-results', 'sidebar', 'browse') */
    context?: 'search-results' | 'sidebar' | 'browse';
    /** The position/index of the link in the list (1-based) */
    position?: number;
    /** The content ID/ARI of the linked content */
    linkContentAri?: string;
    /** The resolved URL of the linked content */
    linkContentResolvedUrl?: string;
    /** The type of parent page */
    parentPageType?: 'content-hub' | 'topic-set';
}

/**
 * Builds enhanced analytics properties for content links
 */
function buildContentLinkAnalytics(
    href: string | null,
    contentLinkAnalytics: ContentLinkAnalytics
) {
    const {
        linkType,
        context,
        position,
        linkContentAri,
        linkContentResolvedUrl,
        parentPageType
    } = contentLinkAnalytics;

    const linkContentEntryId = linkContentAri
        ? extractContentfulEntryId(linkContentAri)
        : null;
    const resolvedUrl = resolveAnalyticsUrl(linkContentResolvedUrl);

    return {
        event: 'clicked',
        eventComponent: 'content-link',
        eventContainer: context,
        ...(position !== undefined && { position }),
        ...(linkType && { link_type: linkType }),
        ...(linkContentAri && { link_content_ari: linkContentAri }),
        ...(linkContentEntryId && {
            link_content_entry_id: linkContentEntryId
        }),
        ...(resolvedUrl && { link_content_resolved_url: resolvedUrl }),
        ...(parentPageType && { parent_page_type: parentPageType }),
        clickTarget: href
    };
}

/**
 * Builds standard analytics properties for regular links
 */
function buildStandardAnalytics(href: string | null, name: string) {
    let target;
    let actionSubjectId;
    if (name.match(/.*button/i)) {
        target = 'button';
        actionSubjectId = camelCase(name.replace(/button/i, ''));
    } else if (name.match(/.*card/i)) {
        target = 'card';
        actionSubjectId = camelCase(name.replace(/card/i, ''));
    } else {
        target = 'link';
        actionSubjectId = camelCase(name);
    }
    return { target, actionSubjectId, clickTarget: href };
}

/*
 adds logic to add required details
 and fires analytics tracking
 @param {string} href - target url
 @param {string} text - optional, link text
 @param {string} name - optional, item name, used for populating actionSubjectId and target
 @param {ContentLinkAttributes} - optional, includes content_id, content_ari, link type
 */
export function trackLink(params: {
    href: string | null;
    text?: string;
    name?: string;
    contentLinkAnalytics?: ContentLinkAnalytics;
}) {
    const { href, text, name, contentLinkAnalytics } = params;

    if (contentLinkAnalytics) {
        const enhancedProperties = buildContentLinkAnalytics(
            href,
            contentLinkAnalytics
        );

        if (name) {
            const { target, actionSubjectId } = buildStandardAnalytics(
                href,
                name
            );
            return analytics.trackEvent('content-link clicked', {
                ...enhancedProperties,
                actionSubjectId,
                target
            });
        }

        return analytics.trackEvent('content-link clicked', enhancedProperties);
    }

    if (name) {
        const { target, actionSubjectId, clickTarget } = buildStandardAnalytics(
            href,
            name
        );
        return analytics.processEvent(target, '', {
            actionSubjectId,
            clickTarget
        });
    }

    analytics.trackLinkClick(href, text);
}

// Backward compatible wrapper
const track = (href: string | null, text?: string, name?: string) => {
    trackLink({ href, text, name });
};

export default track;
